import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { FolderOpen, Users } from 'lucide-react';
import { assignmentState } from '../../lib/assignmentState';

const STATUS_CLS = {
  assigned: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-amber-100 text-amber-800',
  unassigned: 'bg-slate-100 text-slate-700',
};

/**
 * Who is holding this asset, shown inline on a list row.
 *
 * Every holder is its own chip so a van load out to three fitters wraps onto
 * as many lines as it needs instead of running off the side of the table.
 */
export const AssignmentCell = ({ asset, compact = false }) => {
  const { t } = useTranslation();
  const { status, current, upcoming, last, daysOverdue } = assignmentState(asset);
  const statusLabel = t(`inv.overview.status.${status}`, { defaultValue: status });

  return (
    <div className={`min-w-0 ${compact ? 'text-xs' : ''}`} data-testid={`assignment-cell${compact ? '-compact' : ''}-${asset.inventory_number}`}>
      {current.length > 0 ? (
        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
          {current.map((a) => (
            <span key={a.id} className="inline-flex min-w-0 items-center gap-1">
              {a.assignment_type === 'project'
                ? <FolderOpen className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                : <Users className="h-3.5 w-3.5 shrink-0 text-violet-600" />}
              <span className="break-words">{a.target_name || a.target_id}</span>
            </span>
          ))}
        </div>
      ) : last ? (
        <span className="break-words text-muted-foreground">
          {t('inv.overview.notBookedBack', { name: last.target_name || last.target_id })}
        </span>
      ) : (
        <span className="text-muted-foreground">{statusLabel}</span>
      )}

      {(current.length > 0 || last) && (
        <div className="mt-0.5 flex flex-wrap items-center gap-1">
          <Badge className={`text-xs ${STATUS_CLS[status]}`}>{statusLabel}</Badge>
          {daysOverdue > 0 && (
            <span className="text-xs text-amber-700">{t('inv.overview.daysLate', { count: daysOverdue })}</span>
          )}
        </div>
      )}

      {upcoming.length > 0 && (
        <span className="mt-0.5 block break-words text-xs text-muted-foreground">
          {t('inv.overview.nextUp', {
            name: upcoming[0].target_name || upcoming[0].target_id,
            date: String(upcoming[0].date_from || '').slice(0, 10),
          })}
        </span>
      )}
    </div>
  );
};

export default AssignmentCell;
